import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Eagerly loaded: pages needed for first paint (public + layout shell)
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import DashboardLayout from './layouts/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import { UsageProvider } from './context/UsageContext';

// Code-split: secondary pages load on demand when the user navigates
// to them, keeping the initial JS bundle small.
const CasesPage = lazy(() => import('./pages/CasesPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const TimeTrackingPage = lazy(() => import('./pages/TimeTrackingPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const TrustPage = lazy(() => import('./pages/TrustPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientPortalPage = lazy(() => import('./pages/ClientPortalPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage'));
const DocumentTemplatesPage = lazy(() => import('./pages/DocumentTemplatesPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const StaffInviteAcceptPage = lazy(() => import('./pages/StaffInviteAcceptPage'));
const AdminPortalPage = lazy(() => import('./pages/AdminPortalPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AffiliatesPage = lazy(() => import('./pages/AffiliatesPage'));

import './App.css';

// Loader shown while a lazy page chunk is downloading. Keeps layout
// stable so the sidebar doesn't reflow.
function PageFallback() {
  return (
    <div className="p-8 flex items-center justify-center">
      <div className="animate-pulse h-32 w-full max-w-3xl bg-muted rounded-md" />
    </div>
  );
}

// Protected Route component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If user data passed from AuthCallback, render immediately
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public route - redirect to dashboard if already logged in
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for OAuth token (returning from Google callback)
  if (location.hash?.includes('token=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/staff-invite" element={<Suspense fallback={<PageFallback />}><StaffInviteAcceptPage /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<PageFallback />}><PrivacyPolicyPage /></Suspense>} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Dashboard routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <UsageProvider>
              <DashboardLayout />
            </UsageProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="cases" element={<Suspense fallback={<PageFallback />}><CasesPage /></Suspense>} />
        <Route path="documents" element={<Suspense fallback={<PageFallback />}><DocumentsPage /></Suspense>} />
        <Route path="time" element={<Suspense fallback={<PageFallback />}><TimeTrackingPage /></Suspense>} />
        <Route path="billing" element={<Suspense fallback={<PageFallback />}><BillingPage /></Suspense>} />
        <Route path="trust" element={<Suspense fallback={<PageFallback />}><TrustPage /></Suspense>} />
        <Route path="tasks" element={<Suspense fallback={<PageFallback />}><TasksPage /></Suspense>} />
        <Route path="calendar" element={<Suspense fallback={<PageFallback />}><CalendarPage /></Suspense>} />
        <Route path="clients" element={<Suspense fallback={<PageFallback />}><ClientsPage /></Suspense>} />
        <Route path="portal" element={<Suspense fallback={<PageFallback />}><ClientPortalPage /></Suspense>} />
        <Route path="team" element={<Suspense fallback={<PageFallback />}><TeamPage /></Suspense>} />
        <Route path="admin" element={<Suspense fallback={<PageFallback />}><AdminPortalPage /></Suspense>} />
        <Route path="audit" element={<Suspense fallback={<PageFallback />}><AuditLogPage /></Suspense>} />
        <Route path="ai" element={<Suspense fallback={<PageFallback />}><AIAssistantPage /></Suspense>} />
        <Route path="templates" element={<Suspense fallback={<PageFallback />}><DocumentTemplatesPage /></Suspense>} />
        <Route path="integrations" element={<Suspense fallback={<PageFallback />}><IntegrationsPage /></Suspense>} />
        <Route path="help" element={<Suspense fallback={<PageFallback />}><HelpPage /></Suspense>} />
        <Route path="affiliates" element={<Suspense fallback={<PageFallback />}><AffiliatesPage /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageFallback />}><SettingsPage /></Suspense>} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
